import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiRefreshCw, FiEye, FiDownload, FiFileText, FiSearch } from 'react-icons/fi';

const POLICY_CATEGORIES = ['General', 'Leave', 'Attendance', 'Payroll', 'Code of Conduct', 'IT & Security', 'Other'];

// Server origin for opening uploaded files (baseURL is ".../api")
const SERVER_ORIGIN = api.defaults.baseURL.replace(/\/api\/?$/, '');

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PolicyList = () => {
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [form, setForm] = useState({ title: '', description: '', category: 'General', file: null });

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const res = await api.get('/policies', { params });
      setPolicies(res.data);
    } catch (err) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPolicies(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPolicies();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error('Please choose a document to upload');
    try {
      setUploading(true);
      const data = new FormData();
      data.append('title', form.title);
      data.append('description', form.description);
      data.append('category', form.category);
      data.append('file', form.file);
      await api.post('/policies', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Policy uploaded');
      setForm({ title: '', description: '', category: 'General', file: null });
      setShowForm(false);
      fetchPolicies();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this policy document?')) return;
    try {
      await api.delete(`/policies/${id}`);
      toast.success('Policy deleted');
      fetchPolicies();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const fileLink = (p) => `${SERVER_ORIGIN}${p.fileUrl}`;

  return (
    <div>
      <div className="page-header">
        <h1>HR Policies</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {POLICY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="btn-secondary" onClick={fetchPolicies} title="Refresh"><FiRefreshCw /></button>
          {isAdminHR && (
            <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
              <FiPlus /> {showForm ? 'Cancel' : 'Upload Policy'}
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', maxWidth: '400px' }}>
        <input
          placeholder="Search policies..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn-secondary"><FiSearch /></button>
      </form>

      {showForm && isAdminHR && (
        <div className="form-card" style={{ marginTop: '1rem' }}>
          <h3>Upload Policy Document</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Title</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {POLICY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label>Document (PDF, DOC, DOCX, TXT — max 10MB)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={e => setForm({ ...form, file: e.target.files[0] || null })}
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Policy'}
            </button>
          </form>
        </div>
      )}

      {loading ? <div className="loading">Loading...</div> : (
        <table className="table" style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Title</th>
              <th>Category</th>
              <th>Description</th>
              <th>File</th>
              <th>Size</th>
              <th>Uploaded By</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p, index) => (
              <tr key={p._id}>
                <td>{index + 1}</td>
                <td><strong>{p.title}</strong></td>
                <td><span className="status-badge">{p.category}</span></td>
                <td>{p.description || '-'}</td>
                <td><FiFileText style={{ verticalAlign: 'middle' }} /> {p.fileName}</td>
                <td>{formatSize(p.fileSize)}</td>
                <td>{p.uploadedBy?.name || '-'}</td>
                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                <td className="actions">
                  <a
                    className="btn-sm btn-secondary"
                    href={fileLink(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View"
                  ><FiEye /></a>
                  <a
                    className="btn-sm btn-secondary"
                    href={fileLink(p)}
                    download={p.fileName}
                    title="Download"
                  ><FiDownload /></a>
                  {isAdminHR && (
                    <button className="btn-sm btn-danger" onClick={() => handleDelete(p._id)} title="Delete"><FiTrash2 /></button>
                  )}
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center' }}>No policies uploaded yet</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PolicyList;
